import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, InputAdornment, Paper, TextField, Tooltip, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import axios from 'axios';
import { usePermissions } from '../../../utils/usePermissions';
import { notify } from '../../notifications';
import { ReportSecurityWrapper } from './ReportSecurityWrapper';
import { ChangeRow, ConfirmDialog, EditEmptyState, LimitChip, PharmacyAutocomplete, ScopeChip } from './edit/EditCommon';
import NumberInput from './edit/NumberInput';
import ReportHistoryButton from './history/ReportHistoryButton';
import {
  apiErrorMessage, DayResponse, EDIT_API, EditPharmacy, SaveResponse, useEditAccess
} from './edit/editApi';
import {
  Calculator, createCalculator, DayValues, EditField, END_FIELD, findOverflow, formatDateRu, formatNumber,
  getTodayStr, getYesterdayStr, groupFields, isYmd, weekdayShort
} from './edit/editCalc';

const BORDER = '1px solid #e2e8f0';

const KIND_CAPTIONS: Record<string, string> = {
  carry: 'Переносится с предыдущего дня',
  formula: 'Рассчитывается автоматически',
  // external: 'Поступает из внешней системы'
};

/** Редактирование дневного отчета аптеки: правка вводимых значений, мгновенный пересчет итогов, сохранение с пересчетом следующих дней */
const EditReport: React.FC = () => {
  const { hasPerm } = usePermissions();
  const canProtectCopy = hasPerm('reports.edit_daily.protect_copy');

  const { access, loading: accessLoading, error: accessError, reload: reloadAccess } = useEditAccess('daily');

  const [pharmacy, setPharmacy] = useState<EditPharmacy | null>(null);
  const [date, setDate] = useState<string>(getYesterdayStr());
  const [data, setData] = useState<DayResponse | null>(null);
  const [values, setValues] = useState<DayValues | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const requestId = useRef(0);
  const pendingChange = useRef<(() => void) | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const today = useMemo(() => getTodayStr(), []);
  const dateLocked = Boolean(access && access.dateLimit === 'yesterday');

  useEffect(() => {
    document.title = 'Редактировать отчет | Zerde';
  }, []);

  // При ограничении «только вчера» дата фиксируется на значении, которое вернул сервер
  useEffect(() => {
    if (access && access.dateLimit === 'yesterday' && access.allowedDate) {
      setDate(access.allowedDate);
    }
  }, [access]);

  const loadDay = useCallback(async (username: string, ymd: string, silent: boolean) => {
    const id = ++requestId.current;
    if (!silent) {
      setLoading(true);
      setLoadError('');
    }
    try {
      const res = await axios.get<DayResponse>(`${EDIT_API}/day`, {
        params: silent ? { username, date: ymd, silent: 1 } : { username, date: ymd }
      });
      if (id !== requestId.current) return;
      if (!res.data || !res.data.success) {
        throw new Error((res.data as unknown as { message?: string })?.message || 'Не удалось загрузить отчет');
      }
      setData(res.data);
      setValues(res.data.values);
    } catch (err) {
      if (id !== requestId.current) return;
      if (silent) {
        notify.warning('Данные сохранены, но обновить форму не удалось. Откройте отчет заново');
      } else {
        setData(null);
        setValues(null);
        setLoadError(apiErrorMessage(err, 'Не удалось загрузить отчет'));
      }
    } finally {
      if (id === requestId.current && !silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!pharmacy || !isYmd(date)) {
      requestId.current++;
      setData(null);
      setValues(null);
      setLoadError('');
      setLoading(false);
      return;
    }
    loadDay(pharmacy.username, date, false);
  }, [pharmacy, date, reloadKey, loadDay]);

  const calcState = useMemo<{ calc: Calculator | null; error: string }>(() => {
    if (!data) return { calc: null, error: '' };
    try {
      return { calc: createCalculator(data.fields, data.computeOrder), error: '' };
    } catch (err) {
      return { calc: null, error: err instanceof Error ? err.message : 'Не удалось разобрать формулы отчета' };
    }
  }, [data]);

  const visibleFields = useMemo<EditField[]>(
    () => (data ? data.fields.filter((f) => f.kind !== 'meta') : []),
    [data]
  );
  const groups = useMemo(() => groupFields(visibleFields), [visibleFields]);

  // Порядковые номера редактируемых полей: по ним работает переход по Enter
  const inputIndex = useMemo(() => {
    const map: Record<string, number> = {};
    let i = 0;
    groups.forEach((g) => g.fields.forEach((f) => {
      if (f.kind === 'input') map[f.name] = i++;
    }));
    return map;
  }, [groups]);

  const changedFields = useMemo<EditField[]>(() => {
    if (!data || !values) return [];
    return data.fields.filter((f) => f.kind === 'input' && values[f.name] !== data.values[f.name]);
  }, [data, values]);

  const dirty = changedFields.length > 0;
  const overflowField = data && values ? findOverflow(data.fields, values) : null;

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  /** Смена аптеки или даты при несохраненных правках требует подтверждения */
  const guardedChange = (apply: () => void) => {
    if (dirty) {
      pendingChange.current = apply;
      setDiscardOpen(true);
    } else {
      apply();
    }
  };

  const handlePharmacyChange = (next: EditPharmacy | null) => guardedChange(() => setPharmacy(next));

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (dateLocked || !isYmd(next) || next > today) return;
    guardedChange(() => setDate(next));
  };

  const confirmDiscard = () => {
    const apply = pendingChange.current;
    pendingChange.current = null;
    setDiscardOpen(false);
    if (apply) apply();
  };

  const closeDiscard = () => {
    pendingChange.current = null;
    setDiscardOpen(false);
  };

  const handleFieldCommit = (name: string, value: number) => {
    if (!calcState.calc) return;
    const calc = calcState.calc;
    setValues((prev) => (prev ? calc.compute({ ...prev, [name]: value }, null) : prev));
  };

  const handleReset = () => {
    if (data) setValues(data.values);
  };

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    const row = (e.target as HTMLElement).getAttribute('data-r');
    if (row === null || !formRef.current) return;
    const next = formRef.current.querySelector<HTMLInputElement>(`input[data-r="${Number(row) + 1}"]`);
    if (next) {
      e.preventDefault();
      next.focus();
    }
  };

  const handleSave = async () => {
    if (!data || !values) return;
    const payload: DayValues = {};
    changedFields.forEach((f) => { payload[f.name] = values[f.name]; });

    setSaving(true);
    try {
      const res = await axios.put<SaveResponse>(`${EDIT_API}/day`, {
        username: data.pharmacy.username,
        date: data.date,
        values: payload
      });
      if (!res.data || !res.data.success) {
        throw new Error((res.data && res.data.message) || 'Не удалось сохранить отчет');
      }
      notify.success(res.data.message || 'Данные сохранены');
      setConfirmOpen(false);
      await loadDay(data.pharmacy.username, data.date, true);
    } catch (err) {
      setConfirmOpen(false);
      notify.error(apiErrorMessage(err, 'Не удалось сохранить отчет'));
    } finally {
      setSaving(false);
    }
  };

  const endField = data ? data.fields.filter((f) => f.name === END_FIELD)[0] : undefined;
  const endChanged = Boolean(data && values && endField && values[END_FIELD] !== data.values[END_FIELD]);

  const renderField = (field: EditField) => {
    if (!data || !values) return null;
    const isInput = field.kind === 'input';
    const changed = isInput && values[field.name] !== data.values[field.name];
    const derived = !isInput && values[field.name] !== data.values[field.name];
    return (
      <Box
        key={field.name}
        sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 150px', gap: 1.5, alignItems: 'center' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.88rem', fontWeight: field.important ? 700 : 500, color: '#1e293b', lineHeight: 1.3 }}>
            {field.label}
          </Typography>
          {!isInput && KIND_CAPTIONS[field.kind] && (
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.25 }}>{KIND_CAPTIONS[field.kind]}</Typography>
          )}
        </Box>
        <NumberInput
          field={field}
          value={values[field.name]}
          readOnly={!isInput}
          disabled={saving}
          changed={changed}
          derived={derived}
          strong={field.important || field.name === END_FIELD}
          live
          row={isInput ? inputIndex[field.name] : undefined}
          col={isInput ? 0 : undefined}
          onCommit={(v) => handleFieldCommit(field.name, v)}
        />
      </Box>
    );
  };

  const renderBody = () => {
    if (accessLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (accessError || !access) {
      return (
        <EditEmptyState
          icon={<ErrorOutlineIcon />}
          title="Не удалось открыть редактирование"
          text={accessError || 'Нет данных о доступе'}
          action={<Button variant="outlined" onClick={reloadAccess} sx={{ textTransform: 'none', fontWeight: 600 }}>Повторить</Button>}
        />
      );
    }
    if (access.users.length === 0) {
      return (
        <EditEmptyState
          icon={<StorefrontOutlinedIcon />}
          title="Нет доступных аптек"
          text="В вашей области видимости нет аптек для редактирования отчетов."
        />
      );
    }
    if (!pharmacy) {
      return (
        <EditEmptyState
          icon={<StorefrontOutlinedIcon />}
          title="Выберите аптеку"
          text="После выбора аптеки и даты откроется отчет для редактирования."
        />
      );
    }
    if (!isYmd(date)) {
      return <EditEmptyState icon={<ErrorOutlineIcon />} title="Укажите дату отчета" />;
    }
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (loadError) {
      return (
        <EditEmptyState
          icon={<ErrorOutlineIcon />}
          title="Отчет не загружен"
          text={loadError}
          action={
            <Button variant="outlined" onClick={() => setReloadKey((k) => k + 1)} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Повторить
            </Button>
          }
        />
      );
    }
    if (calcState.error) {
      return <EditEmptyState icon={<ErrorOutlineIcon />} title="Ошибка в формулах отчета" text={calcState.error} />;
    }
    if (!data || !values) return null;

    return (
      <Box ref={formRef} onKeyDown={handleFormKeyDown}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.05rem' }}>
            Аптека {data.pharmacy.pharmacyNum} &middot; {formatDateRu(data.date)}, {weekdayShort(data.date)}
          </Typography>
          {!data.exists && (
            <Chip
              label="Записи за эту дату нет"
              size="small"
              sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', backgroundColor: '#f1f5f9', border: BORDER }}
            />
          )}
        </Box>
        {!data.exists && (
          <Typography sx={{ fontSize: '0.84rem', color: '#64748b', mb: 1.5 }}>
            Запись будет создана при сохранении. Остаток на начало дня берется из предыдущего отчета.
          </Typography>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(380px, 1fr))' },
            gap: 2,
            alignItems: 'start'
          }}
        >
          {groups.map((group) => (
            <Paper key={group.name} elevation={0} sx={{ border: BORDER, borderRadius: '12px', overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.25, backgroundColor: '#f8fafc', borderBottom: BORDER }}>
                <Typography sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {group.name}
                </Typography>
              </Box>
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {group.fields.map(renderField)}
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Панель действий */}
        <Paper
          elevation={0}
          sx={{
            position: 'sticky',
            bottom: 8,
            zIndex: 2,
            mt: 2,
            px: 2,
            py: 1.25,
            border: BORDER,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)'
          }}
        >
          <Typography sx={{ fontSize: '0.86rem', color: dirty ? '#92400e' : '#64748b', fontWeight: dirty ? 600 : 400 }}>
            {overflowField
              ? `Поле «${overflowField.label}»: результат выходит за допустимый диапазон`
              : dirty
                ? `Изменено полей: ${changedFields.length}`
                : 'Изменений нет'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            onClick={handleReset}
            disabled={!dirty || saving}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#475569' }}
          >
            Сбросить
          </Button>
          <Button
            variant="contained"
            onClick={() => setConfirmOpen(true)}
            disabled={!dirty || saving || Boolean(overflowField)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none', minWidth: 190 }}
          >
            Сохранить и пересчитать
          </Button>
        </Paper>
      </Box>
    );
  };

  return (
    <ReportSecurityWrapper protectCopy={canProtectCopy} username={pharmacy ? pharmacy.username : undefined}>
      <Box>
        <Paper
          elevation={0}
          sx={{ p: 2, mb: 2, border: BORDER, borderRadius: '12px', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}
        >
          <PharmacyAutocomplete
            options={access ? access.users : []}
            value={pharmacy}
            onChange={handlePharmacyChange}
            loading={accessLoading}
            disabled={saving}
          />
          <Tooltip title={dateLocked ? 'Политика роли: доступен только вчерашний день' : ''} arrow>
            <span>
              <TextField
                size="small"
                label="Дата отчета"
                type="date"
                value={date}
                onChange={handleDateChange}
                disabled={dateLocked || saving}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: today }}
                InputProps={{
                  endAdornment: dateLocked ? (
                    <InputAdornment position="end">
                      <LockIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                    </InputAdornment>
                  ) : undefined
                }}
                sx={{ minWidth: 170 }}
              />
            </span>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <ReportHistoryButton screen="edit_day" pharmacy={pharmacy ? pharmacy.username : null} period={date} />
          {access && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LimitChip limit={access.dateLimit} />
              <ScopeChip scope={access.scope} />
            </Box>
          )}
        </Paper>

        {renderBody()}
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title="Сохранить изменения"
        confirmText="Сохранить"
        loading={saving}
        onConfirm={handleSave}
        onClose={() => setConfirmOpen(false)}
      >
        {data && values && (
          <>
            <Typography sx={{ fontSize: '0.88rem', color: '#475569', mb: 1.5 }}>
              Аптека {data.pharmacy.pharmacyNum}, отчет за {formatDateRu(data.date)}.
              Остатки и формулы следующих дней будут пересчитаны автоматически.
            </Typography>
            <Box sx={{ border: BORDER, borderRadius: '8px', px: 1.5, py: 0.5 }}>
              {changedFields.map((f) => (
                <ChangeRow
                  key={f.name}
                  label={f.label}
                  from={formatNumber(data.values[f.name], f)}
                  to={formatNumber(values[f.name], f)}
                />
              ))}
              {endChanged && endField && (
                <ChangeRow
                  label={`${endField.label} (итог)`}
                  from={formatNumber(data.values[END_FIELD], endField)}
                  to={formatNumber(values[END_FIELD], endField)}
                />
              )}
            </Box>
          </>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={discardOpen}
        title="Несохраненные изменения"
        confirmText="Отменить изменения"
        cancelText="Продолжить редактирование"
        maxWidth="xs"
        onConfirm={confirmDiscard}
        onClose={closeDiscard}
      >
        <Typography sx={{ fontSize: '0.9rem', color: '#475569' }}>
          В отчете есть несохраненные изменения. Если продолжить, они будут потеряны.
        </Typography>
      </ConfirmDialog>
    </ReportSecurityWrapper>
  );
};

export default EditReport;
